<template>
  <div class="login">
    <div class="login-header">
      <div class="login-header__content">
        <img
          class="login-header__logo"
          src="https://cdn-image.bestdealer.com/app-bestdealer/1688/login/logo.png"
          alt="bestdealer logo"
        >
        <p class="login-header__title">
          <img
            class="login-header__icon"
            src="@/assets/colorIcon/lock.svg"
          >
          Your information is strictly protected.
        </p>
        <img
          class="login-header__banner"
          src="https://cdn-image.bestdealer.com/app-bestdealer/1688/login/coupon-banner.png"
          alt="coupon promotions"
        >
      </div>
    </div>
    <div class="login-content">
      <p class="login-content__title">
        Enter Email
      </p>
      <EmailField
        class="login-content__input"
        v-model="form.email"
      />
      <p class="login-content__title">
        Enter Verification Code
      </p>
      <VerificationCodeField
        class="login-content__input"
        v-model="form.code"
      />
      <Button
        class="login-content__continue"
        :disabled="disabled"
        :loading="loading"
        @click="submit"
        type="primary"
      >
        Continue
      </Button>
      <div class="login-content__divider">
        or
      </div>
      <GoogleSingIn />
    </div>
  </div>
</template>

<script setup>
import EmailField from './components/EmailField/index.vue'
import VerificationCodeField from './components/VerificationCodeField/index.vue'
import GoogleSingIn from './components/GoogleSignIn/index.vue'
import { Button } from '@/components/Button'
import { validateEmail, buriedPointUtil, gaAnalytics, isMobile } from '@/utils/utils'
import { usePlugin } from '@/hooks/usePlugin'
import { useLogin } from './hooks/useLogin'

const emit = defineEmits(['loggedIn'])

const { login } = useLogin()
const route = useRoute()
const router = useRouter()
const { $toast } = usePlugin()

const store = useStore()
const passiveLogin = computed(() => store.state.passiveLogin)

/** 配置根据 email / password 自动填充 */
const form = reactive({
  email: route.query.email,
  code: route.query.password
})
const loading = ref(false)
const disabled = computed(() => {
  if (!validateEmail(form.email)) return true
  if (form.code?.length !== 6) return true
  return false
})

const submit = async () => {
  try {
    if (isMobile()) {
      gaAnalytics('mp_login_click', 'mp_login_click', {
        passiveLogin: passiveLogin.value,
        loginType: 'account'
      })
      buriedPointUtil({ eventId: 'MPLG202' })
    } else {
      gaAnalytics('p_login_click', 'p_login_click', {
        passiveLogin: passiveLogin.value,
        loginType: 'account'
      })
      buriedPointUtil({ eventId: 'PLG202' })
    }

    loading.value = true

    const isSuperUser = !!route.query.sp

    const data = await login({
      loginAccount: form.email,
      verifyCode: form.code,
      superuser: isSuperUser
    })

    emit('loggedIn', data)

    /** 登录完成后清除表单信息 */
    form.code = ''
    form.email = ''

    if (isSuperUser) {
      router.push('/')
      return
    }

    /**
     * TODO: 历史逻辑，待 Product 优化后调整
     */
    if (route.fullPath.includes('/guide')) {
      // 引导页登陆成功后重置搜索信息。
      store.commit('setSearchInfo', {
        pageNow: 1,
        pageSize: 50,
        keyword: '',
        imageId: 0,
        region: ''
      })
    }
  } catch (e) {
    console.error(e)
    $toast(e.message, { type: 'error' })
  } finally {
    loading.value = false
  }
}
</script>

<style lang="less" scoped>
.login {
  background-color: #fff;

  &-header {
    position: relative;
    /** 修复某些奇怪场景下，::after元素无法完全覆盖底部的问题 */
    padding-bottom: 1px;
    overflow: hidden;

    &__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 32px 0;
      background:
        url('https://cdn-image.bestdealer.com/app-bestdealer/1688/coupon/gift-bg.png') no-repeat top right / 120px 120px,
        linear-gradient(180deg, #EFF4FD 0%, rgba(176, 199, 245, 0.8) 100%);
    }

    &__logo {
      display: block;
      height: 24px;
      width: 116px;
    }

    &__icon {
      font-size: 12px;
    }

    &__title {
      margin-top: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      font-family: mon-500;
      font-size: 11px;
    }

    &__banner {
      margin-top: 20px;
      display: block;
      height: 112px;
      width: 337px;
    }

    &::after {
      content: '';
      position: absolute;
      height: 1280px;
      width: 1280px;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      background: radial-gradient(circle at top center, transparent 87%, #fff 87%);
      filter: drop-shadow(0px -4px 8px rgba(142, 33, 15, 0.12));
    }
  }

  &-content {
    padding: 0 20px 20px;
    background-color: #fff;

    &__title {
      font-family: mon-600;
      font-size: 14px;

      ~ .login-content__title {
        margin-top: 20px;
      }
    }

    :deep(&__input) {
      margin-top: 8px;
    }

    &__continue {
      margin-top: 32px;
    }

    &__divider {
      margin: 8px auto;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 101px;
      height: 16px;
      color: #b2b7c2;
      font-size: 12px;
      font-family: mon-500;

      &::before, &::after {
        content: '';
        position: absolute;
        width: 32px;
        height: 1px;
        background-color: #ebebeb;
      }

      &::before {
        left: 0;
      }

      &::after {
        right: 0;
      }
    }
  }
}
</style>
